import { localStorageSetItemWithQuotaRecovery } from './localStorageQuota';

export type MemberProfileDisplayMode = 'photo' | 'svg';

const MODE_KEY_PREFIX = 'cp-member-profile-display-mode:';

const getModeStorageKey = (workspaceId: string, gradeId: string) =>
  `${MODE_KEY_PREFIX}${workspaceId}:${gradeId}`;

export const MEMBER_PROFILE_DISPLAY_MODE_CHANGED_EVENT = 'cp:member-profile-display-mode-changed';

export const readMemberProfileDisplayMode = (
  workspaceId: string,
  gradeId: string
): MemberProfileDisplayMode => {
  if (typeof window === 'undefined') return 'svg';
  if (!workspaceId.trim() || !gradeId.trim()) return 'svg';
  try {
    const raw = window.localStorage.getItem(getModeStorageKey(workspaceId, gradeId));
    return raw === 'photo' ? 'photo' : 'svg';
  } catch {
    return 'svg';
  }
};

export const writeMemberProfileDisplayMode = (
  workspaceId: string,
  gradeId: string,
  mode: MemberProfileDisplayMode
): void => {
  if (typeof window === 'undefined') return;
  if (!workspaceId.trim() || !gradeId.trim()) return;
  try {
    localStorageSetItemWithQuotaRecovery(getModeStorageKey(workspaceId, gradeId), mode);
    window.dispatchEvent(
      new CustomEvent(MEMBER_PROFILE_DISPLAY_MODE_CHANGED_EVENT, {
        detail: { workspaceId, gradeId, mode },
      })
    );
  } catch {
    // ignore
  }
};

export const isExternalProfileImageSource = (value?: string | null): boolean => {
  if (!value) return false;
  return (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('blob:') ||
    value.startsWith('data:image/')
  );
};

/** 사용자 프로필 카드 등 member-profile-images 경로를 profileImage에 저장할 때 쓰는 접두사 */
export const MEMBER_PROFILE_STORAGE_PATH_PREFIX = 'mpath:';

export const toMemberProfileStorageImageRef = (storagePath: string): string =>
  `${MEMBER_PROFILE_STORAGE_PATH_PREFIX}${storagePath.trim()}`;

export const parseMemberProfileStorageImageRef = (value?: string | null): string | null => {
  if (!value || !value.startsWith(MEMBER_PROFILE_STORAGE_PATH_PREFIX)) return null;
  const path = value.slice(MEMBER_PROFILE_STORAGE_PATH_PREFIX.length).trim();
  return path || null;
};

export const isMemberProfileStorageImageRef = (value?: string | null): boolean =>
  Boolean(parseMemberProfileStorageImageRef(value));

/**
 * SVG 기본 프로필로 표시할 이미지 ID를 반환한다.
 * 실사 사진(photo 모드)·사용자 프로필 카드(mpath:)는 비공개 버킷이라 signed URL이 필요하므로
 * `useMemberPhotoSignedUrl`(비동기)로 별도 조회해야 한다. 여기서는 svg/공개 URL 폴백만 처리한다.
 */
export const resolveMemberProfileSource = (member: { profileImage?: string }): string =>
  member.profileImage || 'basic-0';
